import type { JSONSchema7 } from "json-schema";

import { propertyHasFormat } from "./schema-property-utils";

export interface ResultProperty {
  key: string;
  property: JSONSchema7;
  isFile: boolean;
}

export interface ResultItem {
  type: "file" | "table";
  properties: Record<string, JSONSchema7>;
}

/**
 * Groups properties by type, respecting order
 * Files are kept separate, consecutive non-file properties (2+) are grouped into tables
 */
export function groupResultProperties(
  schema: Record<string, JSONSchema7>,
  order?: string[]
): ResultItem[] {
  // Respect ui:order if provided, otherwise use object key order
  const keys = order && order.length > 0 ? order : Object.keys(schema);

  // Filter to only keys that exist in schema
  const validKeys = keys.filter((key) => key in schema);

  const items: ResultItem[] = [];
  let tableGroup: { keys: string[]; properties: Record<string, JSONSchema7> } | null = null;

  for (const key of validKeys) {
    const property = schema[key];
    const isFile =
      propertyHasFormat(property, "rana_file") || propertyHasFormat(property, "rana_directory");

    if (isFile) {
      // Flush any accumulated table group
      if (tableGroup && tableGroup.keys.length >= 2) {
        items.push({
          type: "table",
          properties: tableGroup.properties,
        });
      }
      tableGroup = null;

      // Add file as its own item
      items.push({
        type: "file",
        properties: { [key]: property },
      });
    } else {
      // Accumulate non-file properties
      if (!tableGroup) {
        tableGroup = { keys: [], properties: {} };
      }
      tableGroup.keys.push(key);
      tableGroup.properties[key] = property;
    }
  }

  // Flush remaining table group if it has 2+ items
  if (tableGroup && tableGroup.keys.length >= 2) {
    items.push({
      type: "table",
      properties: tableGroup.properties,
    });
  }

  return items;
}
